import { roundedClassname } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    selectCredentials,
    selectCurrentAccountName,
    selectCurrentEntityName,
    selectCurrentLuceraAccountName,
    setCurrentAccount,
    setCurrentAccountName,
    setCurrentEntityName,
    setCurrentLuceraAccount,
    setCurrentLuceraAccountName
} from '@/state/reducers/authSlice';
import { openHelper } from '@/state/reducers/helperSlice';
import {
    openNotifications,
    selectNotificationsOpened,
    setNotificationsVisited
} from '@/state/reducers/notificationSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { navigateAccordingToAccountType } from '@/utils/navigation';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { SSOLoadingState, useSso } from '@/utils/providers/SSOProvider';
import { Menu } from '@headlessui/react';
import { default as cn } from 'classnames';
import { ButtonHTMLAttributes, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { LuUser, LuUserCircle, LuUserSquare } from 'react-icons/lu';
import { MdExpandMore, MdOutlineHelpOutline } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { DropDown } from '../common/Dropdown';
import NotificationButton from '../common/NotificationButton';
import { Ping } from './MobileDrawer';
import { onUserChange } from './Navbar';

export const ssoDisabledMessage = (isLoadingSSO: SSOLoadingState) => {
    if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
    if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
    return 'This feature requires SSO to be enabled.';
};

interface DesktopNavbarProps {
    isPortal?: boolean;
}

const DesktopNavbar = ({ isPortal = false }: DesktopNavbarProps) => {
    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);
    const currentAccountName = useAppSelector(selectCurrentAccountName);
    const notificationsOpened = useAppSelector(selectNotificationsOpened);
    const currentEntityName = useAppSelector(selectCurrentEntityName);
    const currentLuceraAccountName = useAppSelector(selectCurrentLuceraAccountName);

    const { flattenEntities } = useSso();

    const navigate = useNavigate();
    const pathname = window.location.pathname;
    const canOpenHelper = pathname === '/' || pathname.startsWith('/trader');

    const convertedAccounts = useMemo(() => {
        return (
            credentials?.accounts?.map((account) => ({
                code: account.code,
                name: account.name,
                properties: account.properties,
                entityName: flattenEntities?.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        account.name
                )?.entityName
            })) || []
        );
    }, [credentials, flattenEntities]);

    const luceraAccounts = useMemo(() => {
        return (
            credentials?.luceraAccounts?.map((account) => ({
                code: account.code,
                name: account.name
            })) || []
        );
    }, [credentials]);

    const gcexAccountName = useMemo(() => {
        if (flattenEntities && !currentEntityName) {
            return (
                flattenEntities.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        currentAccountName
                )?.entityName || currentAccountName
            );
        } else return currentAccountName;
    }, [flattenEntities, currentAccountName, currentEntityName]);

    return (
        <div className="hidden lg:flex flex-row h-full items-center gap-3 py-2">
            {/* <ThemeButton />
            <LanguageButton /> */}
            <div data-onboarding="notifications">
                <NotificationButton
                    size="w-5 h-5"
                    isActive={notificationsOpened}
                    onClick={() => {
                        dispatch(setNotificationsVisited(true));
                        dispatch(openNotifications());
                    }}
                />
            </div>
            {canOpenHelper && (
                <button
                    type="button"
                    className={cn(
                        'relative text-left text-sm flex gap-1 items-center text-brand-text transition duration-150 ease-in-out whitespace-nowrap lg:my-0 bg-brand-background-dark border border-brand-neutral-700 p-1 rounded-md hover:border-brand-neutral-600 pl-1.5 pr-2.5'
                    )}
                    onClick={() => dispatch(openHelper())}>
                    <MdOutlineHelpOutline className="w-5 h-5" />
                    {lang.navigation.helper}
                </button>
            )}

            <DropDown
                label={lang.common.switchAccount}
                searchable
                menuItemClassName="overflow-y-auto max-h-60"
                button={<MenuButton label={currentLuceraAccountName} Icon={LuUserSquare} />}>
                {(query) => {
                    const filteredAccounts = luceraAccounts.filter((account) =>
                        (account.name || account.code)?.toLowerCase().includes(query.toLowerCase())
                    );

                    if (filteredAccounts.length === 0) {
                        return <div className="px-4 py-2 text-sm text-brand-neutral-400">No results found</div>;
                    }

                    return filteredAccounts.map((account, i) => {
                        return (
                            <MenuItemButton
                                key={i}
                                label={account.name || account.code}
                                Icon={LuUser}
                                onClick={() => {
                                    dispatch(setCurrentLuceraAccount(account.code));
                                    dispatch(setCurrentLuceraAccountName(account.name));
                                }}
                            />
                        );
                    });
                }}
            </DropDown>
            <DropDown
                label={lang.common.switchAccount}
                searchable
                menuItemClassName="overflow-y-auto max-h-60"
                button={<MenuButton label={currentEntityName || gcexAccountName} Icon={LuUserSquare} />}>
                {(query) => {
                    const filteredAccounts = convertedAccounts.filter((account) =>
                        (account.entityName || account.name)?.toLowerCase().includes(query.toLowerCase())
                    );

                    if (filteredAccounts.length === 0) {
                        return <div className="px-4 py-2 text-sm text-brand-neutral-400">No results found</div>;
                    }

                    return filteredAccounts.map((account, i) => {
                        const disabled = !flattenEntities?.find((entity) =>
                            entity.nameMappings?.find((name) => name.source === 'CELER' && name.name === account.name)
                        );
                        return (
                            <MenuItemButton
                                key={i}
                                label={account.entityName || account.name}
                                Icon={LuUser}
                                onClick={() => {
                                    dispatch(setCurrentAccount(account.code));
                                    dispatch(setCurrentAccountName(account.name));
                                    dispatch(setCurrentEntityName(account.entityName || ''));
                                    onUserChange(credentials, dispatch);
                                    navigateAccordingToAccountType(account, navigate);
                                }}
                                disabled={disabled && isPortal}
                            />
                        );
                    });
                }}
            </DropDown>
            <LoggedInUser />
        </div>
    );
};

export default DesktopNavbar;

interface MenuButtonProps {
    Icon?: IconType;
    label?: string;
    className?: string;
}

export const MenuButton = (props: MenuButtonProps) => {
    const { Icon, label, className } = props;

    const { mobile, tablet, interim } = useScreenSizes();
    const isMobile = useMemo(() => mobile || tablet || interim, [mobile, tablet, interim]);
    return (
        <Menu.Button
            title={label}
            className={cn(
                'flex w-56 gap-2 text-sm items-center px-2 justify-between text-brand-text bg-brand-background-dark focus-visible:outline-none',
                {
                    'p-2': isMobile,
                    'p-1': !isMobile
                },
                roundedClassname,
                className
            )}>
            {Icon && (
                <span className="">
                    <Icon className="h-5 w-5" />
                </span>
            )}
            <div className="truncate whitespace-nowrap">{label}</div>
            <span className="ml-auto">
                <MdExpandMore className="h-5 w-5" />
            </span>
        </Menu.Button>
    );
};

interface MenuItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    Icon?: IconType;
    icon?: any;
    label: string;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
}

export const MenuItemButton = (props: MenuItemButtonProps) => {
    const { icon, Icon, label, disabledMessage, isLoadingSSO = 'idle', requiresSSO = false, ...restProps } = props;
    const { disabled } = restProps;

    const ssoTooltip = useMemo(() => {
        if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
        if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
        return '';
    }, [isLoadingSSO]);

    const tooltip = useMemo(() => {
        if (disabled) return ssoTooltip || disabledMessage;
        return ssoTooltip;
    }, [disabled, disabledMessage, ssoTooltip]);

    return (
        <Menu.Item>
            <button
                type="button"
                title={tooltip || label}
                className={cn('group flex w-full gap-2 items-center p-2 text-sm bg-brand-background-dark', {
                    'text-brand-neutral-400 cursor-not-allowed': disabled,
                    'text-brand-text hover:bg-brand-primary hover:text-brand-module-title': !disabled
                })}
                {...restProps}>
                {Icon ? (
                    <span>
                        <Icon className="h-5 w-5" />
                    </span>
                ) : null}
                {icon ? <span>{icon}</span> : null}
                <div className="flex-1 min-w-0 relative flex items-center">
                    {requiresSSO && (isLoadingSSO === 'pending' || isLoadingSSO === 'error') && (
                        <Ping isLoadingSSO={isLoadingSSO} />
                    )}
                    <div
                        className={cn('truncate whitespace-nowrap overflow-hidden', {
                            'pl-4': requiresSSO && ['pending', 'error'].includes(isLoadingSSO)
                        })}
                        title={label} // tooltip for long text
                    >
                        {label}
                    </div>
                </div>
            </button>
        </Menu.Item>
    );
};

export const LoggedInUser = () => {
    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);

    const { mobile, tablet, interim } = useScreenSizes();
    const isMobile = useMemo(() => mobile || tablet || interim, [mobile, tablet, interim]);
    return (
        <div
            className={cn(
                'relative inline-block text-left border border-brand-neutral-700 hover:border-brand-neutral-600 transition duration-150 bg-brand-background',
                roundedClassname
            )}>
            <div className="relative flex items-center">
                <span className="pointer-events-none absolute -top-[8.5px] left-0.5 z-10 px-1.5 text-2xs font-bold text-brand-neutral-400 after:content-[''] after:absolute after:left-[2px] after:right-[2px] after:top-1/2 after:h-[1px] after:bg-brand-background after:-z-10">
                    {lang.common.user}
                </span>

                <div
                    className={cn(
                        'flex w-48 gap-2 text-sm items-center px-2 text-brand-text bg-brand-background-dark',
                        {
                            'p-2': isMobile,
                            'p-1': !isMobile
                        },
                        roundedClassname
                    )}>
                    <span>
                        <LuUserCircle className="h-5 w-5" />
                    </span>
                    <div className="truncate whitespace-nowrap">{credentials?.username}</div>
                </div>
            </div>
        </div>
    );
};
